import { useParams, Link, Navigate } from "react-router-dom";
import SEOHead, { breadcrumbSchema, faqSchema, organizationSchema, softwareApplicationSchema } from "@/components/SEOHead";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import FaqBlock, { CtaBlock } from "@/components/FaqBlock";
import { USE_CASES } from "@/lib/seoData";
import { Check } from "lucide-react";

export default function UseCasePage() {
  const { slug } = useParams();
  const data = USE_CASES.find((u) => u.slug === slug);
  if (!data) return <Navigate to="/" replace />;

  const faqs = buildFaqs(data);
  const path = `/use-cases/${data.slug}`;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title={data.title}
        description={data.description}
        path={path}
        schemas={[
          organizationSchema(),
          softwareApplicationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Use cases", path: "/use-cases/task-management-software" },
            { name: data.h1, path },
          ]),
          faqSchema(faqs),
        ]}
      />
      <MarketingHeader />

      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16 w-full">
        <nav className="text-xs text-zinc-500 mb-6">
          <Link to="/" className="hover:text-zinc-900">Home</Link>{" / "}
          <span className="text-zinc-700">{data.h1.split(":")[0]}</span>
        </nav>

        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight mb-6">
          {data.h1}
        </h1>
        <p className="text-base md:text-lg text-zinc-600 leading-relaxed max-w-3xl">{data.intro}</p>

        {/* AI-extractable summary block */}
        <div className="border-l-2 border-[#002FA7] bg-zinc-50 px-5 py-4 my-10">
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-zinc-500 mb-2">In short</p>
          <p className="text-sm text-zinc-800 leading-relaxed">{data.description}</p>
        </div>

        <h2 className="text-2xl font-semibold text-zinc-900 mt-12 mb-4">Why BreakItDown for {data.keywords[0]}</h2>
        <ul className="space-y-3">
          {data.bullets.map((b, i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-zinc-700 leading-relaxed">
              <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.5} />
              {b}
            </li>
          ))}
        </ul>

        <h2 className="text-2xl font-semibold text-zinc-900 mt-12 mb-4">Explore other use cases</h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {USE_CASES.filter((u) => u.slug !== data.slug).slice(0, 6).map((u) => (
            <Link
              key={u.slug}
              to={`/use-cases/${u.slug}`}
              className="border border-zinc-200 px-3 py-2 text-sm hover:border-zinc-900 transition-colors"
            >
              {u.h1.split(":")[0].split(" — ")[0]}
            </Link>
          ))}
        </div>

        <FaqBlock faqs={faqs} />
        <CtaBlock />
      </main>

      <MarketingFooter />
    </div>
  );
}

function buildFaqs(data) {
  return [
    {
      q: `Is BreakItDown good for ${data.keywords[0]}?`,
      a: `Yes — BreakItDown was built around the exact pain points of ${data.keywords[0]}. The AI takes the cognitive load off planning, the focus mode protects deep work, and the entire app is free.`,
    },
    {
      q: `How is BreakItDown different from a regular to-do list?`,
      a: `Regular to-do lists ask you to write every step yourself. BreakItDown uses Claude Sonnet 4.5 to do the breakdown — type the big task, click Break Down, and you get a checklist in seconds.`,
    },
    {
      q: `Do I need to install anything?`,
      a: `No. BreakItDown is a web app — it works in any modern browser on desktop or mobile.`,
    },
    {
      q: `Is the AI included?`,
      a: `Yes. AI breakdowns and the daily briefing are all included free.`,
    },
    {
      q: `Can I share a task with someone without an account?`,
      a: `Yes. Use the per-task share button to generate a read-only public link. Revoke any time.`,
    },
  ];
}
